import React, { useState, useRef, useEffect } from 'react';
import useDebounce from  '@/components/hooks/useDebounce';
import { BuildPath } from  '@/helpers/Paths';


const AutoComplete = ({initialValue="", setValue, options, updateOptions, isLoading = false}) => {
    const [hasResults, setHasResults] = useState<boolean>(false);
    const [inputInFocus, setInputInFocus] = useState<boolean>(false);
    const [textValue, setTextValue] = useState<string>(initialValue);
    const debounceValue = useDebounce(textValue, 500);

    const [focusedLiIndex, setFocusedLiIndex] = useState<number>(-1);

    const textInput = useRef<HTMLInputElement>(null);
    const focusedListItem = useRef<HTMLLIElement>(null);
    const autoCompleteList = useRef<HTMLUListElement>(null);
    
    useEffect(() => { 
        document.addEventListener('mousedown', handleClick , false);
        return () =>  document.removeEventListener('mousedown', handleClick, false)
    },[])
    
    useEffect(() => {
        if (focusedLiIndex >= 0 && focusedListItem) {
            focusedListItem.current.focus();
        }
    }, [focusedLiIndex])

    useEffect(()=>{
        setTextValue(initialValue);
    },[initialValue])

    useEffect(()=>{
        if (inputInFocus && debounceValue) {
            updateOptions(debounceValue);
        }
    },[debounceValue, inputInFocus])

    useEffect(()=> {
        setHasResults(!!options.length)
    }, [options])

    useEffect(() => {
        setValue(debounceValue)
    },[debounceValue])

    function handleClick(e: any) {
        const clickTarget = e.target as HTMLElement
    
        if(clickTarget.closest('.auto-complete-wrapper')) {
            if (clickTarget.classList.contains('auto-complete-list-option')) {
                setValue(clickTarget.innerText);
            }
        }
        setInputInFocus(false);
        setFocusedLiIndex(-1);
        //setValue(textValue);
    }

    function targetNode(index: number) {
        if(autoCompleteList.current) {
            focusedListItem.current = autoCompleteList.current.childNodes[index] as HTMLLIElement
            setFocusedLiIndex(index);
        }
    }

    function handleKeyPressInTextBox(e: React.KeyboardEvent<HTMLInputElement>) {
        switch(e.key) {
            case 'ArrowUp':
                e.preventDefault();
                setInputInFocus(false);
                setFocusedLiIndex(-1);
                break;
            case 'ArrowDown':
                e.preventDefault();
                targetNode(0);
                break;
            case 'Escape':
            case 'Enter':
                setInputInFocus(false);
                setFocusedLiIndex(-1);
                setValue(textValue);
                break;
            default:
                break;
        }
    }

    function showAutoCompelete() {
        return hasResults && inputInFocus && debounceValue
    }

    function handleKeyPressOnListItem(e: React.KeyboardEvent<HTMLLIElement>, idx: number, value: string) {
        const noOfItems = autoCompleteList.current.childNodes.length
        switch(e.key) {
            case 'ArrowUp':
                e.preventDefault();
                targetNode(idx === 0 ? noOfItems -1 : idx -1);
                break;
            case 'ArrowDown':
                e.preventDefault();
                targetNode(idx === noOfItems -1 ? 0 : idx +1);
                break;
            case 'Home':
                targetNode(0);
                break;
            case 'End':
                targetNode(noOfItems -1);
                break;
            case 'Enter':
                setInputInFocus(false);
                setValue(value);
                setFocusedLiIndex(-1);
                textInput.current.blur();
                break;
            case 'Escape':
                textInput.current.focus();
                setFocusedLiIndex(-1);
                break;
            default: 
                break;
        }
    }

    return (
        <div className="auto-complete-wrapper">
            <div style={{position:'relative'}}>
                <input
                    id="batch-name-input"
                    type="text"
                    ref={textInput}
                    className={`batch-name-input ${textValue && textValue.length > 256 ? 'invalid' : ''}`}
                    placeholder="Please name your batch"
                    value={textValue}
                    autoComplete="off"
                    aria-autocomplete="list"
                    role="combobox"
                    maxLength={256}
                    onChange={e => {
                        setTextValue(e.target.value);
                        setInputInFocus(true);
                    }}
                    onKeyDown={handleKeyPressInTextBox}
                    onFocus={()=> {
                        setInputInFocus(true)
                        setValue(textValue)
                    }}

                    //onBlur={()=> setValue(textValue)}
                />
                <span className='char-count'> {textValue && textValue.length ? textValue.length : 0} / 256</span>
                {isLoading && <img className="auto-complete-loading" src={`${BuildPath()}/images/userupload/rolling.svg`} alt="loading-spinner" />}
            </div>
            <div className={`auto-complete-options ${showAutoCompelete() ? 'open': 'closed'}`}>
                {showAutoCompelete() &&
                    <ul role="list-box" ref={autoCompleteList} className={isLoading ? 'loading' : ''}>
                        {options
                        .map((value: string, idx: number) => (
                            <li 
                                tabIndex={0}
                                key={idx}
                                role={'option'}
                                className={`auto-complete-list-option ${idx === focusedLiIndex ? 'highlight' : ''}`}
                                onClick={() => {
                                    setValue(value);
                                    setInputInFocus(false);
                                    setFocusedLiIndex(-1);
                                }}
                                onKeyDown={(e) => handleKeyPressOnListItem(e, idx, value)}
                            >
                            <span>{value}</span>
                            </li>)
                        )}
                    </ul>}
                    <div aria-live="polite" role="status" className="visually-hidden">
  	                    {options.length} results available.
                    </div>
            </div>
        </div>
    )
}

export default AutoComplete